// ---------------------------------------------------------------------------
// Test Mode — simulated invoices (50+) for Clean Code Culture SRL (CLE-250).
//
// Income invoices are issued to clients for project work; outcome invoices come
// from the recurring suppliers. Totals tie back to the financials P&L and the
// per-client invoice counts shown in the Clients module.
// ---------------------------------------------------------------------------

import type { Invoice, InvoiceStatus } from '../lib/invoicesApi';
import { isoDate, isoDateTime, eur } from './company';
import { CLIENT_SEEDS } from './clients';
import { SUPPLIER_SEEDS } from './suppliers';
import { PROJECT_SEEDS } from './projects';
import { testCompanyTag } from './companies';

const VAT = 0.19;

function withVat(net: number) {
  const taxAmount = Math.round(net * VAT);
  return { amount: net, taxAmount, amountTotal: net + taxAmount };
}

let cached: Invoice[] | null = null;

export function buildInvoices(): Invoice[] {
  if (cached) return cached;
  const invoices: Invoice[] = [];
  let seq = 1000;

  // --- Income: a few invoices per client that has projects/revenue ----------
  const billedClients = CLIENT_SEEDS.filter((c) => c.totalInvoiced > 0);
  billedClients.forEach((client, ci) => {
    const project = PROJECT_SEEDS.find((p) => p.clientId === client.id) ?? null;
    // Spread the client's total over 2-4 invoices.
    const count = 2 + (ci % 3);
    const per = Math.round(client.totalInvoiced / count);
    for (let i = 0; i < count; i++) {
      seq += 1;
      const paidCount = Math.round((client.totalPaid / Math.max(1, client.totalInvoiced)) * count);
      const isPaid = i < paidCount;
      const issueOffset = -(30 * (count - i));
      const dueOffset = issueOffset + 30;
      const status: InvoiceStatus = isPaid ? 'PAID' : dueOffset < 0 ? 'OVERDUE' : 'SENT';
      const { amount, taxAmount, amountTotal } = withVat(per);
      invoices.push({
        source: 'manual',
        efacturaMessageId: null,
        issuedInvoice: null,
        items: [],
        id: `inv-in-${seq}`,
        invoiceNumber: `TF-${seq}`,
        direction: 'INCOME',
        status,
        vendor: null,
        client: client.name,
        currency: 'RON',
        amount,
        taxAmount,
        amountTotal,
        issueDate: isoDate(issueOffset),
        dueDate: isoDate(dueOffset),
        paidAt: isPaid ? isoDateTime(dueOffset - 2, 12) : null,
        notes: project ? `Servicii dezvoltare — ${project.name}` : 'Servicii dezvoltare software',
        overdue: status === 'OVERDUE',
        project: project ? { id: project.id, name: project.name } : null,
        serviceId: null,
        service: null,
        // Income invoices inherit the firm from their client.
        ...testCompanyTag(client.id),
        fileName: `factura-TF-${seq}.pdf`,
        fileType: 'application/pdf',
        s3Key: null,
        s3Url: null,
        createdAt: isoDateTime(issueOffset, 9),
        updatedAt: isoDateTime(issueOffset, 9),
      });
    }
  });

  // --- Outcome: recurring supplier invoices for the last 3 months -----------
  SUPPLIER_SEEDS.filter((s) => s.isRecurring).forEach((supplier, si) => {
    for (let m = 1; m <= 3; m++) {
      seq += 1;
      const issueOffset = -30 * m;
      const { amount, taxAmount, amountTotal } = withVat(eur(supplier.monthlyEur));
      invoices.push({
        source: 'manual',
        efacturaMessageId: null,
        issuedInvoice: null,
        items: [],
        id: `inv-out-${seq}`,
        invoiceNumber: `${supplier.cui.slice(-4)}-${m}${si}`,
        direction: 'OUTCOME',
        status: 'PAID',
        vendor: supplier.name,
        client: null,
        currency: 'RON',
        amount,
        taxAmount,
        amountTotal,
        issueDate: isoDate(issueOffset),
        dueDate: isoDate(issueOffset + 15),
        paidAt: isoDateTime(issueOffset + 10, 12),
        notes: supplier.notes,
        overdue: false,
        project: null,
        serviceId: null,
        service: null,
        // Outcome invoices inherit the firm from their supplier.
        ...testCompanyTag(supplier.id),
        fileName: `${supplier.name.split(' ')[0]}-${m}.pdf`,
        fileType: 'application/pdf',
        s3Key: null,
        s3Url: null,
        createdAt: isoDateTime(issueOffset, 9),
        updatedAt: isoDateTime(issueOffset, 9),
      });
    }
  });

  cached = invoices;
  return invoices;
}

export function invoiceCountsByClient(): Record<string, number> {
  const counts: Record<string, number> = {};
  const byName = new Map(CLIENT_SEEDS.map((c) => [c.name, c.id]));
  for (const inv of buildInvoices()) {
    if (inv.direction !== 'INCOME' || !inv.client) continue;
    const id = byName.get(inv.client);
    if (id) counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export function buildInvoice(id: string): Invoice | null {
  return buildInvoices().find((i) => i.id === id) ?? null;
}

/** Client invoices filtered by clientId (income invoices matching the client name).
 *  Returns AggregatedInvoice shape (source + paidAmount) expected by ClientInvoicesTab. */
export function buildClientInvoices(clientId: string) {
  const client = CLIENT_SEEDS.find((c) => c.id === clientId);
  if (!client)
    return {
      invoices: [],
      totals: { currency: 'RON', totalInvoiced: 0, totalPaid: 0, outstanding: 0 },
    };
  const raw = buildInvoices().filter((i) => i.direction === 'INCOME' && i.client === client.name);
  const invoices = raw.map((i) => ({
    id: i.id,
    source: 'db' as const,
    invoiceNumber: i.invoiceNumber,
    direction: i.direction,
    status: i.status,
    amount: i.amountTotal,
    paidAmount: i.status === 'PAID' ? i.amountTotal : 0,
    currency: i.currency,
    issueDate: i.issueDate,
    dueDate: i.dueDate,
    overdue: i.overdue,
    project: i.project,
    s3Key: i.s3Key ?? null,
    s3Url: null,
  }));
  const totalInvoiced = invoices.reduce((s, i) => s + i.amount, 0);
  const totalPaid = invoices.filter((i) => i.status === 'PAID').reduce((s, i) => s + i.amount, 0);
  return {
    invoices,
    totals: {
      currency: 'RON',
      totalInvoiced,
      totalPaid,
      outstanding: Math.max(0, totalInvoiced - totalPaid),
    },
  };
}

/** Person invoices — income invoices where the person is assigned to the project. */
export function buildPersonInvoices(personId: string) {
  // Find projects where this person is a member.
  const personProjects = new Set(
    PROJECT_SEEDS.filter((p) => p.memberIds.includes(personId)).map((p) => p.id),
  );
  const invoices = buildInvoices().filter(
    (i) => i.direction === 'INCOME' && i.project && personProjects.has(i.project.id),
  );
  return invoices;
}

/** SmartBill-format invoices — wraps buildInvoices() in SmartBill shape. */
export function buildSmartbillInvoices() {
  return buildInvoices().map((inv, i) => ({
    id: `sb-${inv.id}`,
    series: inv.invoiceNumber.split('-')[0] ?? 'TF',
    number: inv.invoiceNumber.split('-')[1] ?? String(i),
    direction: inv.direction === 'INCOME' ? 'ISSUED' : 'RECEIVED',
    clientName: inv.client ?? inv.vendor ?? '',
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    netAmount: String(inv.amount),
    vatAmount: String(inv.taxAmount),
    totalAmount: String(inv.amountTotal),
    currency: inv.currency,
    paymentStatus: inv.status === 'PAID' ? 'PAID' : inv.overdue ? 'OVERDUE' : 'UNPAID',
    association: inv.project
      ? { type: 'project' as const, id: inv.project.id, name: inv.project.name }
      : null,
    createdAt: inv.createdAt,
  }));
}

/** SmartBill summary — aggregate by project/person/category. */
export function buildSmartbillSummary() {
  const invoices = buildInvoices();
  const income = invoices.filter((i) => i.direction === 'INCOME');
  const outcome = invoices.filter((i) => i.direction === 'OUTCOME');

  const byProject: Array<{
    projectId: string;
    projectName: string;
    income: number;
    outcome: number;
    count: number;
  }> = [];
  const projectMap = new Map<
    string,
    { income: number; outcome: number; count: number; name: string }
  >();

  for (const inv of invoices) {
    if (!inv.project) continue;
    const entry = projectMap.get(inv.project.id) ?? {
      income: 0,
      outcome: 0,
      count: 0,
      name: inv.project.name,
    };
    if (inv.direction === 'INCOME') entry.income += inv.amountTotal;
    else entry.outcome += inv.amountTotal;
    entry.count++;
    projectMap.set(inv.project.id, entry);
  }
  for (const [id, entry] of projectMap) {
    byProject.push({ projectId: id, projectName: entry.name, ...entry });
  }

  return {
    byProject,
    byPerson: [],
    byCompanyCategory: [
      {
        category: 'Servicii dezvoltare',
        income: income.reduce((s, i) => s + i.amountTotal, 0),
        outcome: 0,
        count: income.length,
      },
      {
        category: 'Furnizori',
        income: 0,
        outcome: outcome.reduce((s, i) => s + i.amountTotal, 0),
        count: outcome.length,
      },
    ],
    companyTotal: {
      income: income.reduce((s, i) => s + i.amountTotal, 0),
      outcome: outcome.reduce((s, i) => s + i.amountTotal, 0),
      count: invoices.length,
    },
  };
}
